import { LanguagesEnum } from '@/api/enums';
import { CourseContent, Resource } from '@/client/courses';
import { ContinueOrFinishButton } from '@/components/courses/ContinueOrFinishButton';
import { CourseModuleFrame } from '@/components/courses/CourseModuleFrame';
import { ContentPDFViewer } from '@/ui/pdf-viewer/ContentPDFViewer';
import React, { useState } from 'react';

type CoursePdfModuleProps = {
  resource: Resource;
  requiresReadConfirmation: boolean;
  language: LanguagesEnum;
  currentModuleIndex: number;
  modules: CourseContent[];
  isContentStarted: boolean;
  rtl?: boolean;
  accessibilityIcon?: React.ReactNode;
  onContinue: () => void;
  onFinish: () => void;
  onPreviousModule: () => void;
};

export const CoursePdfModule: React.FC<CoursePdfModuleProps> = ({
  resource,
  requiresReadConfirmation,
  language,
  currentModuleIndex,
  modules,
  isContentStarted,
  rtl,
  accessibilityIcon,
  onContinue,
  onFinish,
  onPreviousModule,
}) => {
  const [hasConfirmedRead, setHasConfirmedRead] = useState<boolean>(false);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);

  const file =
    resource.translations?.find((t) => t.language === language)?.file ?? null;

  return (
    <CourseModuleFrame>
      {accessibilityIcon}
      <ContentPDFViewer
        file={file}
        hasPreviousModules={currentModuleIndex > 0}
        hideNextButton={currentModuleIndex === modules.length - 1}
        continueOrFinishButton={
          <ContinueOrFinishButton
            currentModule={currentModuleIndex}
            modules={modules}
            isCurrentModuleCompleted={hasReachedEnd}
            isContentStarted={isContentStarted}
            attemptLanguage={language}
            shouldWrapInStickyContainer={false}
            isDisabled={requiresReadConfirmation && !hasConfirmedRead}
            onContinue={onContinue}
            onFinish={onFinish}
            rtl={rtl}
          />
        }
        requiresReadConfirmation={requiresReadConfirmation}
        hasConfirmedRead={hasConfirmedRead}
        attemptLanguage={language}
        onReadConfirmationChange={setHasConfirmedRead}
        onPreviousModule={onPreviousModule}
        onLastPageReached={() => setHasReachedEnd(true)}
      />
    </CourseModuleFrame>
  );
};
